import { LRFEstimates } from "../../reducers/methodDiscoveryReducer";
import { PlantationApiLocation } from "../api/plantation";

export enum CropType {
  None = "None",
  Wheat = "Wheat",
  Barley = "Barley",
  Maize = "Maize",
  Oats = "Oats",
  Rice = "Rice",
  Sorghum = "Sorghum",
  Triticale = "Triticale",
  OtherCereals = "OtherCereals",
  Pulses = "Pulses",
  TuberAndRoots = "TuberAndRoots",
  SugarCane = "SugarCane",
  Peanuts = "Peanuts",
  Cotton = "Cotton",
  Hops = "Hops",
  OilSeeds = "Oilseeds",
  ForageCrops = "ForageCrops"
}

export enum FuelType {
  None = "None",
  DieselOil = "DieselOil",
  BioDiesel = "Biodiesel",
  Petrol = "Gasoline",
  Ethanol = "EthanolICE",
  OtherBiofuel = "BiofuelExc59And60"
}

export enum LRFProjectType {
  AvoidedDeforestation = "AvoidedDeforestation",
  EnvironmentalPlantings = "EnvironmentalPlantings",
  Revegetation = "Revegetation",
  Soil = "Soil"
}

export type LRFProjectTypeKeys = keyof LRFProjectType;

export const lrfProjectTypeToInt = (t: LRFProjectType): number => {
  switch (t) {
    case LRFProjectType.AvoidedDeforestation:
      return 0;
    case LRFProjectType.EnvironmentalPlantings:
      return 1;
    case LRFProjectType.Revegetation:
      return 2;
    case LRFProjectType.Soil:
      return 3;
  }
};

export const lrfProjectTypeOrd = (
  a: LRFProjectType,
  b: LRFProjectType
): number => lrfProjectTypeToInt(b) - lrfProjectTypeToInt(a);

export enum SoilProjectType {
  NutrientManagementAcidityManagement = "NutrientManagementAcidityManagement",
  NutrientManagementNewIrrigation = "NutrientManagementNewIrrigation",
  NutrientManagementPastureRenovation = "NutrientManagementPastureRenovation",
  AcidityManagementNewIrrigation = "AcidityManagementNewIrrigation",
  AcidityManagementPastureRenovation = "AcidityManagementPasturerenovation", // typo in the python/swagger
  NewIrrigationPastureRenovation = "NewIrrigationPastureRenovation",
  StubbleRetention = "StubbleRetention",
  ConversionToPasture = "ConversionToPasture",
  CarbonSoilSequestration = "CarbonSoilSequestration",
  BeefHerdManagement = "BeefHerdManagement"
}

export type SoilProjectTypeKeys = keyof SoilProjectType;

export const CALCULATED_PROJECTS = [
  SoilProjectType.CarbonSoilSequestration,
  SoilProjectType.BeefHerdManagement
];

export const soilProjectTypeToInt = (t: SoilProjectType): number => {
  switch (t) {
    case SoilProjectType.NutrientManagementAcidityManagement:
      return 0;
    case SoilProjectType.NutrientManagementNewIrrigation:
      return 1;
    case SoilProjectType.NutrientManagementPastureRenovation:
      return 2;
    case SoilProjectType.AcidityManagementNewIrrigation:
      return 3;
    case SoilProjectType.AcidityManagementPastureRenovation:
      return 4;
    case SoilProjectType.NewIrrigationPastureRenovation:
      return 5;
    case SoilProjectType.StubbleRetention:
      return 6;
    case SoilProjectType.ConversionToPasture:
      return 7;
    case SoilProjectType.CarbonSoilSequestration:
      return 8;
    case SoilProjectType.BeefHerdManagement:
      return 9;
  }
};

export const soilProjectTypeOrd = (
  a: SoilProjectType,
  b: SoilProjectType
): number => soilProjectTypeToInt(b) - soilProjectTypeToInt(a);

export const sustainableIntensification: Array<SoilProjectType> = [
  SoilProjectType.NutrientManagementAcidityManagement,
  SoilProjectType.NutrientManagementNewIrrigation,
  SoilProjectType.NutrientManagementPastureRenovation,
  SoilProjectType.AcidityManagementNewIrrigation,
  SoilProjectType.AcidityManagementPastureRenovation,
  SoilProjectType.NewIrrigationPastureRenovation
];

export enum VegProjectType {
  EnvironmentalMalleePlanting = "Environmental Planting",
  HumanInducedRegrowth = "Human Induced Regrowth",
  AvoidedClearingNativeVegetation = "Avoided Clearing Native Vegetation",
  NativeForestManagedRegrowth = "Native Forest from Managed Regrowth"
}

export const vegProjectTypeToInt = (t: VegProjectType): number => {
  switch (t) {
    case VegProjectType.EnvironmentalMalleePlanting:
      return 0;
    case VegProjectType.HumanInducedRegrowth:
      return 1;
    case VegProjectType.AvoidedClearingNativeVegetation:
      return 2;
    case VegProjectType.NativeForestManagedRegrowth:
      return 3;
  }
};

export const vegProjectTypeOrd = (
  a: VegProjectType,
  b: VegProjectType
): number => vegProjectTypeToInt(b) - vegProjectTypeToInt(a);

export enum VegMethod {
  EnvironmentalMalleePlanting = "emp",
  HumanInducedRegrowth = "hir",
  AvoidedClearingNativeVegetation = "acnv",
  NativeForestManagedRegrowth = "nfmr"
}

export enum PlantationMethod {
  PlantationForestActivites = "pfa"
}

export type ProjectType =
  | { kind: "veg"; type: VegProjectType; title: string }
  | { kind: "soil"; type: SoilProjectType; title: string }
  | {
      kind: "plantation";
      type: PlantationProjectType;
      title: string;
      location?: PlantationApiLocation;
    };

export const mapVegMethodToVegProjectType = (method: string) => {
  switch (method) {
    case VegMethod.EnvironmentalMalleePlanting:
      return VegProjectType.EnvironmentalMalleePlanting;
    case VegMethod.HumanInducedRegrowth:
      return VegProjectType.HumanInducedRegrowth;
    case VegMethod.AvoidedClearingNativeVegetation:
      return VegProjectType.AvoidedClearingNativeVegetation;
    case VegMethod.NativeForestManagedRegrowth:
      return VegProjectType.NativeForestManagedRegrowth;
  }
};

export enum PlantationProjectType {
  PlantationForestActivites = "Plantation Forest Activities",
  FarmingForestry = "Establishing a farm forest",
  EstablishingNewPlantationForest = "Establishing a new plantation forest",
  ShortToLongRotation = "Convert short to long rotation",
  ContinuingRotationalHarvest = "Continuing rotational harvest",
  TransitioningToPermanentForest = "Transitioning to permanent forest"
}

export const mapPlantationMethodToPlantationProjectType = (method: string) => {
  switch (method) {
    case PlantationMethod.PlantationForestActivites:
      return PlantationProjectType.PlantationForestActivites;
  }
};

export const checkVegMethodMatchVegProject = (
  method: string,
  projectType: string
) => projectType === mapVegMethodToVegProjectType(method);

export const checkPlantationMethodMatchPlantationProject = (
  method: string,
  projectType: string
) => projectType === mapPlantationMethodToPlantationProjectType(method);

export const Soil = (t: SoilProjectType): ProjectType => ({
  kind: "soil",
  type: t,
  title: "Soil"
});

export const Veg = (t: VegProjectType): ProjectType => ({
  kind: "veg",
  type: t,
  title: "Vegetation"
});

export const Plantation = (
  t: PlantationProjectType,
  l?: PlantationApiLocation
): ProjectType => ({
  kind: "plantation",
  type: t,
  title: "Plantation",
  location: l
});

export const plantationProjectTypeToInt = (
  t: PlantationProjectType
): number => {
  switch (t) {
    case PlantationProjectType.PlantationForestActivites:
      return 0;
    case PlantationProjectType.FarmingForestry:
      return 1;
    case PlantationProjectType.EstablishingNewPlantationForest:
      return 2;
    case PlantationProjectType.ShortToLongRotation:
      return 3;
    case PlantationProjectType.ContinuingRotationalHarvest:
      return 4;
    case PlantationProjectType.TransitioningToPermanentForest:
      return 5;
  }
};

export const plantationProjectTypeOrd = (
  a: PlantationProjectType,
  b: PlantationProjectType
): number => plantationProjectTypeToInt(b) - plantationProjectTypeToInt(a);

export const isEnum = <T extends Record<any, any>>(e: T) => (
  token: any
): token is T[keyof T] => Object.values(e).includes(token as T[keyof T]);

export const toProjectType = (
  t: SoilProjectType | VegProjectType | PlantationProjectType,
  r?: PlantationApiLocation
): ProjectType => {
  if (isEnum(VegProjectType)(t)) {
    return Veg(t);
  } else if (isEnum(SoilProjectType)(t)) {
    return Soil(t);
  } else {
    return Plantation(t, r);
  }
};

export const projectTypeOrd = (a: ProjectType, b: ProjectType): number => {
  if (a.kind === "veg") {
    if (b.kind === "veg") {
      return vegProjectTypeOrd(a.type, b.type);
    } else {
      return -1;
    }
  } else if (a.kind === "soil") {
    if (b.kind === "soil") {
      return soilProjectTypeOrd(a.type, b.type);
    } else {
      return 1;
    }
  } else {
    if (b.kind === "plantation") {
      return plantationProjectTypeOrd(a.type, b.type);
    } else {
      return -1;
    }
  }
};

export const mapFarmProjectTypeToLRFProjectType = (
  farmProjectType: SoilProjectType | VegProjectType | PlantationProjectType
): LRFProjectType | null => {
  switch (farmProjectType) {
    case VegProjectType.AvoidedClearingNativeVegetation:
      return LRFProjectType.AvoidedDeforestation;
    case VegProjectType.EnvironmentalMalleePlanting:
      return LRFProjectType.EnvironmentalPlantings;
    case VegProjectType.HumanInducedRegrowth:
      return LRFProjectType.Revegetation;
    case VegProjectType.NativeForestManagedRegrowth:
      return LRFProjectType.Revegetation;

    case SoilProjectType.BeefHerdManagement:
      return null;
    case PlantationProjectType.PlantationForestActivites:
      return null;

    default:
      return LRFProjectType.Soil;
  }
};

export type Cobenefits = {
  farmCobenefit: FarmCobenefit;
  lrfCobenefit: LRFCobenefit | null;
};

export enum FarmCobenefitStrength {
  None = 0,
  Slight = 1,
  Moderate = 2,
  Strong = 3
}

export type FarmCobenefit = {
  optimisedYield: FarmCobenefitStrength;
  optimisedLandUse: FarmCobenefitStrength;
  optimisedSOC: FarmCobenefitStrength;
  optimisedIncome: FarmCobenefitStrength;

  improvedWater: FarmCobenefitStrength;
  improvedSoil: FarmCobenefitStrength;
  reducedChemicalRunOff: FarmCobenefitStrength;
  reducedSalinity: FarmCobenefitStrength;

  optimisedBiodiversity: FarmCobenefitStrength;
  optimisedAnimalWelfare: FarmCobenefitStrength;
  climateAdaptability: FarmCobenefitStrength;

  landUseConflicts: FarmCobenefitStrength;
  reducedWaterYield: FarmCobenefitStrength;

  // adiitional benefits
  increaseFireRisk?: FarmCobenefitStrength;
  communityBenefit?: FarmCobenefitStrength;
};

export type LRFCobenefit = {
  coastalEcosystems: number;
  greatBarrierReef: number;
  nativeVegetation: number;
  threatenedEcosystems: number;
  threatenedWildlife: number;
  wetlands: number;

  summary: number;
};

export enum LRFCobenefitKeys {
  coastalEcosystems = "coastalEcosystems",
  greatBarrierReef = "greatBarrierReef",
  nativeVegetation = "nativeVegetation",
  threatenedEcosystems = "threatenedEcosystems",
  threatenedWildlife = "threatenedWildlife",
  wetlands = "wetlands"
}

export const lrfCobenefitsKeysOrdered = [
  "greatBarrierReef",
  "wetlands",
  "coastalEcosystems",
  "nativeVegetation",
  "threatenedEcosystems",
  "threatenedWildlife"
];

export const toLRFCobenefitScore = (score: number) => (score < 1 ? 0 : score);

const toFarmScore = (cobenefitStrength: FarmCobenefitStrength) =>
  cobenefitStrength === FarmCobenefitStrength.None ? 0 : 1;

const toFarmCobenefitScore = (score: number) => {
  if (score < 1) {
    return 0;
  } else if (score <= 2) {
    return 1;
  } else if (score <= 4) {
    return 2;
  } else if (score <= 7) {
    return 3;
  } else if (score <= 10) {
    return 4;
  } else {
    return 5;
  }
};

export const calculateFarmCobenefits = (cobenefit: FarmCobenefit): number => {
  const disbenefitKeys: (keyof FarmCobenefit)[] = [
    "landUseConflicts",
    "reducedWaterYield",
    "increaseFireRisk"
  ];
  const score = Object.entries(cobenefit)
    .filter(([key, _]) => !(disbenefitKeys as string[]).includes(key))
    .reduce(
      (sum, [_, cobenefitStrength]) =>
        sum +
        (cobenefitStrength !== undefined ? toFarmScore(cobenefitStrength) : 0),
      0
    );
  const hasDisbenefits = disbenefitKeys.every(
    key => cobenefit[key] !== FarmCobenefitStrength.None
  );

  return hasDisbenefits
    ? toFarmCobenefitScore(score) - 1
    : toFarmCobenefitScore(score);
};

export const calculateLRFCobenefits = (
  lrfProjectType: LRFProjectType,
  estimates: Partial<LRFEstimates>
): number => {
  if (estimates === undefined) return 0;
  else {
    const score = estimates[lrfProjectType]?.summary || 0;
    return score;
  }
};

export type FarmCobenefitKeys = keyof FarmCobenefit;

export enum LivestockType {
  None = "None",
  Beef = "Beef",
  Dairy = "Dairy",
  Sheep = "Sheep",
  Other = "Other"
}

export enum LivestockClass {
  None = "None",
  Ram = "Ram",
  Wether = "Wether",
  MaidenEwe = "MaidenEwe",
  BreedingEwe = "BreedingEwe",
  OtherEwe = "OtherEwe",
  LambHogget = "LambOrHogget",
  Goat = "Goat",
  Horse = "Horse",
  Deer = "Deer",
  Buffalo = "Buffalo",
  DonkeyMule = "Donkeys",
  EmuOstrich = "EmuOrOstrich",
  Alpaca = "Alpaca",
  Camel = "Camel",
  MilkingCow = "MilkingCow",
  HeiferGT1 = "Heifer>1",
  HeiferLT1 = "Heifer<1",
  DairyBullGT1 = "DairyBull>1",
  DairyBullLT1 = "DairyBull<1",
  BullGT1 = "Bull>1",
  BullLT1 = "Bull<1",
  SteerLT1 = "Steer<1",
  SteerGT1 = "Steer>1",
  Cow1To2 = "Cow1To2",
  CowGT2 = "Cow>2",
  CowLT1 = "Cow<1"
}

export const livestockClassesByType = (
  t: LivestockType
): Array<LivestockClass> => {
  switch (t) {
    case LivestockType.None:
      return [LivestockClass.None];
    case LivestockType.Beef:
      return [
        LivestockClass.None,
        LivestockClass.BullGT1,
        LivestockClass.BullLT1,
        LivestockClass.SteerLT1,
        LivestockClass.SteerGT1,
        LivestockClass.Cow1To2,
        LivestockClass.CowGT2,
        LivestockClass.CowLT1
      ];
    case LivestockType.Dairy:
      return [
        LivestockClass.None,
        LivestockClass.MilkingCow,
        LivestockClass.HeiferGT1,
        LivestockClass.HeiferLT1,
        LivestockClass.DairyBullGT1,
        LivestockClass.DairyBullLT1
      ];
    case LivestockType.Sheep:
      return [
        LivestockClass.None,
        LivestockClass.Ram,
        LivestockClass.Wether,
        LivestockClass.MaidenEwe,
        LivestockClass.BreedingEwe,
        LivestockClass.OtherEwe,
        LivestockClass.LambHogget
      ];
    case LivestockType.Other:
      return [
        LivestockClass.None,
        LivestockClass.Goat,
        LivestockClass.Horse,
        LivestockClass.Deer,
        LivestockClass.Buffalo,
        LivestockClass.DonkeyMule,
        LivestockClass.EmuOstrich,
        LivestockClass.Alpaca,
        LivestockClass.Camel
      ];
  }
};

export const livestockClassToString = (c: LivestockClass): string => {
  switch (c) {
    case LivestockClass.None:
      return "None";
    case LivestockClass.Ram:
      return "Ram";
    case LivestockClass.Wether:
      return "Wether";
    case LivestockClass.MaidenEwe:
      return "Maiden Ewe";
    case LivestockClass.BreedingEwe:
      return "Breeding Ewe";
    case LivestockClass.OtherEwe:
      return "Other Ewe";
    case LivestockClass.LambHogget:
      return "Lamb Or Hogget";
    case LivestockClass.Goat:
      return "Goat";
    case LivestockClass.Horse:
      return "Horse";
    case LivestockClass.Deer:
      return "Deer";
    case LivestockClass.Buffalo:
      return "Buffalo";
    case LivestockClass.DonkeyMule:
      return "Donkeys";
    case LivestockClass.EmuOstrich:
      return "Emu Or Ostrich";
    case LivestockClass.Alpaca:
      return "Alpaca";
    case LivestockClass.Camel:
      return "Camel";
    case LivestockClass.MilkingCow:
      return "Milking Cow";
    case LivestockClass.HeiferGT1:
      return "Heifer > 1";
    case LivestockClass.HeiferLT1:
      return "Heifer < 1";
    case LivestockClass.DairyBullGT1:
      return "Dairy Bull > 1";
    case LivestockClass.DairyBullLT1:
      return "Dairy Bull < 1";
    case LivestockClass.BullGT1:
      return "Bull > 1";
    case LivestockClass.BullLT1:
      return "Bull < 1";
    case LivestockClass.SteerLT1:
      return "Steer < 1";
    case LivestockClass.SteerGT1:
      return "Steer > 1";
    case LivestockClass.Cow1To2:
      return "Cow 1 to 2";
    case LivestockClass.CowGT2:
      return "Cow > 2";
    case LivestockClass.CowLT1:
      return "Cow < 1";
  }
};

export enum Production {
  Pasture = "Pasture",
  Crop = "Crop",
  SugarCane = "Sugar Cane",
  Cotton = "Cotton",
  Vegetables = "Vegetables",
  Horticulture = "Horticulture",
  NativeForest = "Native Forest"
}

export enum Month {
  January = "January",
  Feburary = "Feburary",
  March = "March",
  April = "April",
  May = "May",
  June = "June",
  July = "July",
  August = "August",
  September = "September",
  October = "October",
  November = "November",
  December = "December"
}

export enum IntegerMonth {
  January = 1,
  Feburary = 2,
  March = 3,
  April = 4,
  May = 5,
  June = 6,
  July = 7,
  August = 8,
  September = 9,
  October = 10,
  November = 11,
  December = 12
}
